import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Bike } from "lucide-react";
import { BUSINESS } from "../data/business";

const LINKS = [
  { href: "#rostilj", label: "Roštilj" },
  { href: "#meni", label: "Meni" },
  { href: "#prilozi", label: "Prilozi" },
  { href: "#price", label: "Priče" },
  { href: "#galerija", label: "Galerija" },
  { href: "#lokacija", label: "Lokacija" },
];

export const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -76 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollTo(href);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-white/90 backdrop-blur-xl border-b border-black/5 shadow-[0_8px_30px_-18px_rgba(0,0,0,0.25)]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between gap-4">
          <button data-testid="navbar-logo" onClick={() => go("#pocetna")} className="flex items-center gap-3 group shrink-0">
            <img
              src="/images/logo.jpg"
              alt="Do jaja logo"
              className="w-11 h-11 rounded-full object-cover ring-2 ring-ember/25 transition-transform duration-300 group-hover:rotate-6 group-hover:scale-105"
            />
            <span className="font-display font-extrabold uppercase tracking-tight text-lg hidden sm:block">
              <span className="text-ember">Do</span> <span className="text-leafdark">jaja</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-6">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`navbar-${l.label.toLowerCase()}-link`}
                onClick={() => go(l.href)}
                className="font-mono-x text-xs uppercase tracking-[0.18em] text-leafdark hover:text-chili transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <a
              data-testid="navbar-phone-btn"
              href={`tel:${BUSINESS.phones[0].tel}`}
              className="hidden xl:flex items-center gap-2 border border-leaf/35 rounded-full px-4 py-2.5 text-sm font-semibold text-leafdark hover:border-ember hover:text-ember transition-colors duration-300"
            >
              <Phone className="w-4 h-4 text-ember" />
              {BUSINESS.phones[0].label}
            </a>
            <a
              data-testid="navbar-glovo-btn"
              href={BUSINESS.glovo}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden xl:flex items-center gap-2 bg-ember text-white font-semibold text-xs rounded-full px-4 py-2.5 hover:bg-emberglow transition-colors duration-300"
            >
              <Bike className="w-4 h-4" />
              Naruči preko Glovo-a
            </a>
            <a
              data-testid="navbar-wolt-btn"
              href={BUSINESS.wolt}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden xl:flex items-center gap-2 border-2 border-leaf text-leafdark font-semibold text-xs rounded-full px-4 py-2 hover:bg-leaf hover:text-white transition-colors duration-300"
            >
              Naruči preko Wolt-a
            </a>
            <button
              data-testid="navbar-mobile-toggle"
              onClick={() => setOpen(!open)}
              className="xl:hidden w-10 h-10 flex items-center justify-center text-ink"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-white/97 backdrop-blur-2xl flex flex-col items-center justify-center gap-6"
          >
            {LINKS.map((l, i) => (
              <motion.button
                key={l.href}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.06 * i }}
                onClick={() => go(l.href)}
                className={`font-display font-extrabold uppercase text-3xl transition-colors ${
                  i % 2 ? "text-leafdark hover:text-ember" : "text-ember hover:text-leafdark"
                }`}
              >
                {l.label}
              </motion.button>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col items-center gap-3 mt-3"
            >
              {BUSINESS.phones.map((p) => (
                <a key={p.tel} href={`tel:${p.tel}`} className="flex items-center gap-2 text-lg font-semibold text-leafdark">
                  <Phone className="w-5 h-5 text-ember" /> {p.label}
                </a>
              ))}
              <div className="flex flex-col sm:flex-row gap-3 mt-2">
                <a href={BUSINESS.glovo} target="_blank" rel="noopener noreferrer" className="bg-ember text-white font-semibold text-sm rounded-full px-6 py-3 text-center">
                  Naruči preko Glovo-a
                </a>
                <a href={BUSINESS.wolt} target="_blank" rel="noopener noreferrer" className="border-2 border-leaf text-leafdark font-semibold text-sm rounded-full px-6 py-2.5 text-center">
                  Naruči preko Wolt-a
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
